import {
  metersToYards,
  supportedFromTypes,
  supportedLabelTypes,
  convertToMeters,
  metersToKilometers,
  metersToFeet,
  metersToMiles,
  formatNumber,
  getUnitFullNameFromAbbreviation,
} from '@/components/shared/x-distance.vue'
import { STAT_UNITS } from '@/lib/enums'
import type { BBox } from '@/lib/geographic'
import { getAreaOfPolygon, getDistance } from 'geolib'
import type { GeolibInputCoordinates } from 'geolib/es/types'
import round from 'lodash/round'

import { getItem } from '@/plugins/storage'

export type UnitType = 'imperial' | 'metric'

/**
 * Supported length units for conversion.
 * Matches supportedFromTypes from x-distance.vue.
 */
export type SupportedFromTypes = (typeof supportedFromTypes)[number]

export type SupportedLabelTypes = (typeof supportedLabelTypes)[number]
/**
 * Given the array of coordinates for a line, return the total distance
 * of that line. Uses default 1 meter resolution.
 * @param coordinates Array of coordinates for line
 * @returns distance in meters
 */
export const findLineDistance = (coordinates: GeolibInputCoordinates[]) => {
  if (!coordinates || !coordinates.length) {
    return 0
  }

  const sumSegmentLengths = (
    accumulator: number,
    value: GeolibInputCoordinates,
    index: number
  ) => {
    const nextPoint = coordinates[index + 1]
    if (nextPoint) {
      const segmentDistance = getDistance(value, coordinates[index + 1])
      return accumulator + segmentDistance
    }
    return accumulator
  }

  // Calculate the distance between each point and add them together
  return coordinates.reduce(sumSegmentLengths, 0)
}

/**
 * Given the coordinates of a polygon, find the area.
 * @param coordinates array of coordinates for a polygon
 * @param unitType imperial or metric
 * @returns area and unit of measure
 */
export const findArea = (
  coordinates: GeolibInputCoordinates[],
  unitType: UnitType
) => {
  const area = coordinates ? getAreaOfPolygon(coordinates) : 0
  return formatArea(area, unitType)
}

/**
 * Given an area in square meters format/convert the area
 * based on passed unit type
 * @param areaInM2 - area in meters squared
 * @param string - imperial or metric
 * @returns area and unit of measure
 */
export function formatArea(areaInM2: number, unitType: UnitType) {
  let areaString = 0
  let unitOfMeasure = ''
  if (unitType === 'imperial') {
    const areaInAc = areaInM2 * 0.000247105
    areaString = round(areaInAc, 2)
    unitOfMeasure = STAT_UNITS.AC
  } else if (unitType === 'metric') {
    if (areaInM2 > 10000) {
      areaString = round(areaInM2 / 1000000, 2)
      unitOfMeasure = STAT_UNITS.KM2
    } else {
      areaString = round(areaInM2, 1)
      unitOfMeasure = STAT_UNITS.M2
    }
  }
  return { area: areaString, UOM: unitOfMeasure }
}

/**
 * Divides each element of the array by the divisor.
 * @param array Array of number values.
 * @param divisor Divisor to divide array values by.
 */
export const arrayDivide = (array: number[], divisor: number) =>
  array.map((v) => v / divisor)

/**
 * *(AI-generated)*
 * Sets the minimum number of integer digits for display formatting on a numeric value.
 */
export const setMinimumIntegerDigits = (
  value: number,
  numberOfDigits?: number
) => value.toLocaleString(undefined, { minimumIntegerDigits: numberOfDigits })

interface FitUnitLengthArgs {
  /**
   * *(AI-generated)*
   * The initial distance value
   */
  length: number
  /**
   * *(AI-generated)*
   * The initial distance unit (e.g., 'meters', 'm', 'feet', 'ft', etc.). Empty string is the default.
   */
  from: SupportedFromTypes | ''
  /**
   * *(AI-generated)*
   * The unit type
   */
  unitType: UnitType
  /**
   * *(AI-generated)*
   * How to label the distance
   */
  labelType: SupportedLabelTypes
  /**
   * *(AI-generated)*
   * How many fraction digits to include
   */
  maximumFractionDigits: number
}

/**
 * Automatically picks a unit of length based on supplied distance and unit type.
 * Converts input distance to appropriate display unit with vertical-specific behavior.
 *
 * @param length - The distance value to convert
 * @param from - Source unit type (e.g., 'meters', 'feet', 'yards', 'kilometers', 'miles')
 * @param unitType - Target unit system: 'imperial' or 'metric'
 * @param labelType - Label format: 'abbreviation' (ft, m), 'full' (feet, meters), or 'none'
 * @param maximumFractionDigits - Decimal places to display (default 1, feet always use 0)
 *
 * @returns Formatted string with distance and unit (e.g., "150 ft", "1.2 mi", "500 m")
 *
 * Output behavior by unitType and vertical:
 *
 * **Metric (all verticals):**
 * - Distances < 500m then return meters (m)
 * - Distances ≥ 500m then return kilometers (km)
 *
 * **Imperial - Fish vertical
 * - Distances < 100 yards (~91m) then return feet (ft)
 * - Distances 100-1000 yards (~91-914m) then return yards (yds)
 * - Distances ≥ 1000 yards then return miles (mi)
 *
 * @throws {Error} If from is not a supported unit type
 * @throws {Error} If unitType is not 'imperial' or 'metric'
 * @throws {Error} If labelType is not supported
 */
export const getBestFitUnitOfLength = ({
  length = 0,
  from = '',
  unitType,
  labelType = 'abbreviation',
  maximumFractionDigits = 1,
}: Partial<FitUnitLengthArgs> = {}) => {
  if (from === '' || !supportedFromTypes.includes(from)) {
    throw Error('From type not supported')
  }
  if (unitType !== 'imperial' && unitType !== 'metric') {
    throw Error('Unit type must be either imperial or metric')
  }
  if (!supportedLabelTypes.includes(labelType)) {
    throw Error('Label type not supported')
  }

  let distance = 0
  let unit = ''

  const distanceInMeters = convertToMeters(from, length)

  // Metric best fit
  if (unitType === 'metric') {
    if (distanceInMeters < 500) {
      distance = distanceInMeters
      unit = 'm'
    } else {
      distance = metersToKilometers(distanceInMeters)
      unit = 'km'
    }
  }
  // Imperial best fit
  else {
    const distanceInYards = metersToYards(distanceInMeters)
    if (distanceInYards < 100) {
      distance = metersToFeet(distanceInMeters)
      unit = 'ft'
    } else if (distanceInYards < 1000) {
      distance = distanceInYards
      unit = 'yds'
    } else {
      distance = metersToMiles(distanceInMeters)
      unit = 'mi'
    }
  }

  // Never round feet, trying to figure out x/10 to inches is too confusing
  const digits = unit === 'ft' ? 0 : maximumFractionDigits
  distance = formatNumber(digits)(distance)
  if (labelType === 'full') {
    unit = getUnitFullNameFromAbbreviation(unit)
  } else if (labelType === 'none') {
    unit = ''
  }

  return `${distance} ${unit}`
}

/**
 * Remaps a value from one range to another.
 * @param inMin Input range minimun value
 * @param inMax Input range maximun value
 * @param outMin Output range minimun value
 * @param outMax Output range maximun value
 * @return A function that converts a value
 */
export const remapRange = (
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number
) => {
  return (x: number) => {
    return ((x - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin
  }
}

/**
 * Format a number into a readable, comma-separated string 1234 => "1,234".
 * @param num Number to format, integer or float
 * @returns Comma-separated number to max 2 decimal places
 */
export const commaSeparatedNumber = (num: number) =>
  new Intl.NumberFormat().format(num)

/**
 * Downsample an array to a smaller number of values. Returns array if
 * newLength >= array.length.
 * @param originalArray Original array to modify
 * @param newLength What length to downsample array to. Should be less than array.length
 */
export const arraySample = (originalArray: number[], newLength: number) => {
  if (newLength >= originalArray.length) {
    return originalArray
  }
  const factor = originalArray.length / (originalArray.length - newLength)
  return originalArray.filter((_, i) => Math.floor(i % factor))
}

/**
 * Convert a float to it's corresponding hour and minute values.
 * Example: 1.5 -> { hours: 1, minutes: 30 }
 * @param time Floating point value from richContent (such as hikeHours)
 * @returns Keys for hours and minutes (either value can be 0)
 */
export const floatToTime = (time: number) => {
  const hour = Math.floor(time)
  const mins = time - hour
  const minsDisplay = Math.floor(mins * 60)
  return { hours: hour, mins: minsDisplay }
}

/**
 * Rounds a number up to the nearest increment of 15.
 * Will not round an increment of 15 up (ex: roundToFifteens(15) === 15)
 * @param num Number to round
 * @returns Either 15, 30, 45, or 60
 */
export const roundToFifteens = (num: number) => Math.ceil(num / 15) * 15

/**
 * Rounds a number up to the nearest increment of 0.25.
 * @param num Number to round
 * @returns Either 0.25, 1.75, 3.0, etc
 */
export const roundToQuarters = (num: number) => {
  return (Math.ceil(4 * num) / 4).toFixed(2)
}

/**
 * Determine if a number is whole or not.
 * @param num - number to determine if it is whole or not
 */
export const isWholeNumber = (num: number) => num % 1 === 0

/**
 * Convert a longitude and zoom value into a Slippy map Tile name X value.  Based upon
 * https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames#ECMAScript_(JavaScript/ActionScript,_etc.)
 * @param lon longitude value
 * @param zoom map zoom value
 * @returns Slippy map tile name X value
 */
export const longitudeToSlippyTileX = (lon: number, zoom: number) => {
  const min = 0
  const max = Math.pow(2, zoom) - 1

  const x = Math.floor(((lon + 180) / 360) * Math.pow(2, zoom))

  return clampValue(x, min, max)
}

/**
 * Convert a latitude and zoom value into a Slippy map Tile name Y value.  Based upon
 * https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames#ECMAScript_(JavaScript/ActionScript,_etc.)
 * @param lat latitude value
 * @param zoom map zoom value
 * @returns Slippy map tile name Y value
 */
export const latitudeToSlippyTileY = (lat: number, zoom: number) => {
  const min = 0
  const max = Math.pow(2, zoom) - 1

  const y = Math.floor(
    ((1 -
      Math.log(
        Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)
      ) /
        Math.PI) /
      2) *
      Math.pow(2, zoom)
  )
  return clampValue(y, min, max)
}

/**
 * Convert a lat/lon location and map zoom level into a full Slippy map tile name.
 * @param lat latitude value
 * @param lon longitude value
 * @param zoom - map zoom value
 * @returns tileName (formatted Slippy map tile name), z value, x value, y value
 * @throws {Error} if slippy zoom is not integer
 */
export const locationToSlippyTileName = (
  lat: number,
  lon: number,
  zoom: number
) => {
  if (!Number.isInteger(zoom)) {
    throw new Error('slippy zoom is not integer')
  }
  const y = latitudeToSlippyTileY(lat, zoom)
  const x = longitudeToSlippyTileX(lon, zoom)
  return {
    tileName: `${zoom}/${x}/${y}`,
    z: zoom,
    x,
    y,
  }
}

/**
 * Get the value of a number constrained by a min and max
 * @param val - the number to compare to min and max
 * @param min - the minimum number that can be returned
 * @param max - the maximum number that can be returned
 * @returns the number closest to the value constrained by a min and max value
 * @throws {Error} if min is greater than the max
 */
export const clampValue = (val: number, min: number, max: number) => {
  if (min > max) {
    throw new Error('Min is greater then max')
  }

  if (val < min) {
    return min
  } else if (val > max) {
    return max
  }
  return val
}

/**
 * Convert a bounding box and map zoom level into a an array of full Slippy map tile names.
 * @param bbox top, left, bottom, right of the space you want tiles for in lat/lon
 * @param zoom map zoom value
 * @returns array of tileNames (formatted Slippy map tile name), z value, x value, y value
 * @throws {Error} if slippy zoom is not integer
 */
export const bboxToSlippyTileNames = (bbox: BBox, zoom: number) => {
  if (!Number.isInteger(zoom)) {
    throw new Error('slippy zoom is not integer')
  }

  const topLeftX = longitudeToSlippyTileX(bbox.left, zoom)
  const topLeftY = latitudeToSlippyTileY(bbox.top, zoom)

  const bottomRightX = longitudeToSlippyTileX(bbox.right, zoom)
  const bottomRightY = latitudeToSlippyTileY(bbox.bottom, zoom)

  const tiles = []
  for (let x = topLeftX; x <= bottomRightX; x++) {
    for (let y = topLeftY; y <= bottomRightY; y++) {
      tiles.push({
        tileName: `${zoom}/${x}/${y}`,
        z: zoom,
        x,
        y,
      })
    }
  }

  return tiles
}

/**
 * Calculate difference between two timestamps, in seconds.
 * @param startTime Millisecond epoch timestamp to start measurement (ex: Date.now())
 * @returns Time in seconds between start time and now
 */
export const timeDifferenceInSeconds = (startTime: number) => {
  return Math.round((Date.now() - startTime) / 1000) || 0
}

/**
 * Formats a number with an ordinal suffix string (st, nd, rd, th).
 * @param number
 * @returns number with ordinal suffix
 */
export const getOrdinalSuffix = (number: number) => {
  // check for specific case of numbers ending in 11, 12, or 13
  let remainder = number % 100
  if (remainder === 11 || remainder === 12 || remainder === 13) {
    return `${number}th`
  }

  // determine ordinal suffix
  remainder = number % 10
  switch (remainder) {
    case 1:
      return `${number}st`
    case 2:
      return `${number}nd`
    case 3:
      return `${number}rd`
    default:
      return `${number}th`
  }
}

/**
 * Converts a number to alpha chars as if you were counting using the alphabet,
 * starting with A as 0.
 * Ex: A = 0, B = 1, C = 2, BA = 26, BAA = 676
 * @param number number to convert to alpha chars
 * @returns string of alpha chars representing a number
 */
export const convertToAlpha = (number: number) => {
  let alpha = ''
  let dividend = number
  let quotient = Math.floor(dividend / 26)
  while (quotient > 0) {
    // take the remainder, convert to alpha char, and place as next largest digit
    alpha = String.fromCharCode((dividend % 26) + 65) + alpha

    // take the quotient and set it as the new dividend
    dividend = quotient
    quotient = Math.floor(dividend / 26)
  }
  // tack on the last remaining digit
  alpha = String.fromCharCode((dividend % 26) + 65) + alpha

  return alpha
}

/**
 * Format elevation value for display, converting between metric and imperial units
 * based on user preference. Excludes elevation values that are 0 or invalid.
 * @param altitude - Elevation value in meters
 * @returns Formatted elevation string with units, or undefined if invalid/zero
 */
export const formatElevation = (
  altitude: number | null | undefined
): string | undefined => {
  // don't show elevation that is not a number or is 0
  // *** context: older waypoints were getting their elevation set as a default of 0.
  // as a result we have a ton of waypoints in the database with incorrect set elevation.
  // newly created waypoints will have accurate elevation data attached to them. we will
  // exclude showing elevation for those with inaccurate data by omitting 0's with the hypothesis
  // that waypoints with a true elevation of 0 are rare and the elevation is not a value-add
  if (!altitude || isNaN(altitude) || Number(altitude) === 0) {
    return undefined
  }

  let elevation = altitude
  let units = 'm'

  if (getItem('unitType') === 'imperial') {
    elevation = metersToFeet(Number(altitude))
    units = 'ft'
  }

  return `${formatNumber(0)(elevation)} ${units}`
}
